import { useCallback, useEffect, useState } from "react";
import {
  Table,
  Row,
  Col,
  Tooltip,
  User,
  Text,
  Button,
} from "@nextui-org/react";
import { useNotifications } from "reapop";
import { StyledBadge } from "./StyledBadge";
import { IconButton } from "./IconButton";
import { EditIcon } from "./EditIcon";
import { DeleteIcon } from "./DeleteIcon";
import Confirm from "./Confirm";
import DevEdit from "./DevEdit";
import { makeFetch } from "../../service/makeFetch";
import useStorage from "../storage/useLocalStorage";
import devImg from "../../asset/icons8-developer-60.png";
import AddUser from "./AddUser";

export default function ListDevs() {
  const [userAuthenticated = false, setUserAuthenticated] =
    useStorage("userAuthenticated");
  const [makeLogInfoVisible, setMakeLogInfoVisible] = useState(false);
  const [makeConfirmVisible, setMakeConfirmVisible] = useState(false);
  const [makeNewUserVisible, setMakeNewUserVisible] = useState(false);
  const { notify } = useNotifications();
  const [allDevs = [], setAllDevs] = useStorage("allDevs");
  const [userInfo, setUserInfo] = useState(null);
  const [refreshAccount = false, setRefreshAccount] =
    useStorage("refreshAccount");
  const [userRole = "user", setUserRole] = useStorage("userRole", "user");

  useEffect(() => {
    if (userAuthenticated === true) {
      getAllDevs();
    }
  }, [userAuthenticated]);

  useEffect(() => {
    if (refreshAccount === true && userAuthenticated === true) {
      getAllDevs();
    }
  }, [refreshAccount, userAuthenticated]);

  const getAllDevs = async () => {
    const result = await makeFetch({
      url: "developers/find/all",
      method: "GET",
      privateCall: false,
    });
    if (result === undefined || result === null) {
      notify("Error getting developers failed!", "error");
      return;
    }
    if (result?.error === true) {
      notify(result.message, "error");
      return;
    }
    if (typeof window !== "undefined") {
      setAllDevs(result.data || []);
    }
  };

  const columns = [
    { name: "NAME", uid: "name" },
    { name: "ROLE", uid: "role" },
    { name: "STATUS", uid: "status" },
    { name: "ACTIONS", uid: "actions" },
  ];

  const toEditUser = useCallback((e) => {
    console.log("e", e);
    setUserInfo(e);
    setMakeLogInfoVisible(true);
  }, []);

  const toDeleteUser = useCallback((e) => {
    setUserInfo(e);
    setMakeConfirmVisible(true);
  }, []);

  const openNewUserMaker = useCallback((e) => {
    setUserInfo(e);
    setMakeNewUserVisible(true);
  }, []);

  const renderCell = (user, columnKey) => {
    const cellValue = user[columnKey];
    switch (columnKey) {
      case "name":
        return (
          <User squared src={devImg} name={cellValue} css={{ p: 0 }}>
            {user.email}
          </User>
        );
      case "role":
        return (
          <Col>
            <Row>
              <Text b size={14} css={{ tt: "capitalize" }}>
                {cellValue}
              </Text>
            </Row>
            <Row>
              <Text b size={13} css={{ tt: "capitalize", color: "$accents7" }}>
                {user.team}
              </Text>
            </Row>
          </Col>
        );
      case "status":
        return <StyledBadge type={user.status}>{cellValue}</StyledBadge>;

      case "actions":
        return (
          <Row justify="center" align="center">
            <Col css={{ d: "flex" }}>
              <Tooltip content="Edit user">
                <IconButton onClick={(e) => toEditUser(user)}>
                  <EditIcon size={20} fill="#979797" />
                </IconButton>
              </Tooltip>
            </Col>
            {userRole === "admin" && (
              <Col css={{ d: "flex" }}>
                <Tooltip content="Delete user" color="error">
                  <IconButton onClick={(e) => toDeleteUser(user)}>
                    <DeleteIcon size={20} fill="#FF0080" />
                  </IconButton>
                </Tooltip>
              </Col>
            )}
          </Row>
        );
      default:
        return cellValue;
    }
  };

  return (
    <>
      {userRole === "admin" && (
        <Button auto onPress={openNewUserMaker}>
          Add User
        </Button>
      )}
      <AddUser
        makeVisible={makeNewUserVisible}
        setVisible={(e) => {
          setMakeNewUserVisible(e);
        }}
        info={userInfo}
        isAdmin={userRole === "admin"}
        style={{ marginTop: userRole === "admin" ? 20 : 10 }}
      />
      <DevEdit
        makeVisible={makeLogInfoVisible}
        setVisible={(e) => {
          setMakeLogInfoVisible(e);
        }}
        info={userInfo}
        isAdmin={userRole === "admin"}
        style={{ marginTop: 20 }}
      >
        <Table
          css={{
            minWidth: "100%",
          }}
          selectionMode="none"
        >
          <Table.Header columns={columns}>
            {(column) => (
              <Table.Column
                key={column.uid}
                hideHeader={column.uid === "actions"}
                align={column.uid === "actions" ? "center" : "start"}
              >
                {column.name}
              </Table.Column>
            )}
          </Table.Header>
          <Table.Body
            items={
              allDevs === undefined || allDevs === null || allDevs.length === 0
                ? []
                : allDevs
            }
          >
            {(item) => (
              <Table.Row>
                {(columnKey) => (
                  <Table.Cell>{renderCell(item, columnKey)}</Table.Cell>
                )}
              </Table.Row>
            )}
          </Table.Body>
          <Table.Pagination
            shadow
            noMargin
            align="center"
            rowsPerPage={10}
            onPageChange={(page) => console.log({ page })}
          />
        </Table>
      </DevEdit>

      <Confirm
        makeVisible={makeConfirmVisible}
        setVisible={(e) => {
          setMakeConfirmVisible(e);
        }}
        info={userInfo}
      />
    </>
  );
}
